import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  Question,
  Buildings,
  Siren,
  UsersThree,
  HandHeart,
} from "@phosphor-icons/react";

/**
 * FaqHelpDialog — onboarding / how-to-play content.
 *
 * Opened from:
 *   • User dropdown ("FAQ / Help" — between Settings and Enter Fullscreen)
 *   • Automatically on first launch per device (gated by localStorage key)
 *
 * Sections (ordered to match the user's onboarding flow):
 *   1. New Players — first thing to do: place a fire station.
 *   2. Call Spawning — how the queue grows + what gates new call types.
 *   3. Mutual Aid Groups — why joining/forming a group matters.
 */
export default function FaqHelpDialog({ open, onOpenChange }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="faq-help-dialog"
        className="rounded-none border border-red-500/30 bg-[#0F1115] text-white max-w-2xl p-0 max-h-[88vh] grid grid-rows-[auto_minmax(0,1fr)_auto] gap-0"
      >
        <DialogHeader className="px-5 py-3 border-b border-red-500/20 bg-red-500/[0.04]">
          <DialogTitle className="mono uppercase tracking-widest text-[12px] text-red-300 flex items-center gap-2">
            <Question size={14} weight="fill" /> FAQ / Help
            <span className="ml-2 text-zinc-500 text-[10px] tracking-wider font-normal">
              How to Play Dispatch 911
            </span>
          </DialogTitle>
        </DialogHeader>

        <div className="overflow-y-auto px-5 py-4 space-y-5 text-zinc-200 text-sm leading-relaxed">
          {/* --- New Players --- */}
          <Section
            icon={<Buildings size={16} weight="fill" className="text-emerald-400" />}
            title="New Players"
            testid="faq-section-new-players"
          >
            <p>
              <strong className="text-emerald-300">Place a fire station</strong> by
              selecting the <span className="text-zinc-100 font-semibold">Build Facility</span> tab
              and selecting <span className="text-zinc-100 font-semibold">Fire Station</span>.
            </p>
            <p className="text-zinc-400">
              You can then purchase more units and more bays as needed when you grow your fire service.
            </p>
          </Section>

          {/* --- Call Spawning --- */}
          <Section
            icon={<Siren size={16} weight="fill" className="text-red-400" />}
            title="Call Spawning"
            testid="faq-section-call-spawning"
          >
            <p>
              The amount of calls that can spawn at one time are determined by how many
              fire stations you have built <strong className="text-red-300">+1 call</strong>.
              So if you have 3 stations then 4 calls will spawn. If you have 6 stations then 7 calls will spawn.
            </p>
            <p>
              Calls can only spawn if the units required to handle a call are stationed in any of
              the fire stations you have built. Example is hazmat calls won't spawn until a
              <span className="text-zinc-100 font-semibold"> Hazmat unit</span> has been purchased.
            </p>
            <p className="text-zinc-400">
              There is a slight chance for some calls to <span className="text-amber-300 font-semibold">"Upgrade"</span>{" "}
              which may put the unit requirements outside of what the player can handle. This is where joining a
              mutual aid group is a must.
            </p>
          </Section>

          {/* --- Mutual Aid Groups --- */}
          <Section
            icon={<UsersThree size={16} weight="fill" className="text-cyan-400" />}
            title="Mutual Aid Groups"
            testid="faq-section-mutual-aid"
          >
            <p>
              Mutual Aid Groups are an <strong className="text-cyan-300">essential part of this game</strong>.
              You can request assistance with any call by clicking the{" "}
              <span className="text-zinc-100 font-semibold">"Request Help"</span> button inside of a call.
              On the right side of the screen in the{" "}
              <span className="text-zinc-100 font-semibold">Mutual Aid Requests</span> you will see calls that
              other players need assistance on. There will also be markers of what units are currently needed for that call.
            </p>
            <p className="flex items-start gap-2">
              <HandHeart size={14} weight="fill" className="text-emerald-400 mt-0.5 shrink-0" />
              <span>
                If your unit is on scene when the call ends then you also get the cash reward. This is a great
                way to make money for your alliance if people send a unit to just earn the money on large incidents.
              </span>
            </p>
            <p className="text-cyan-300 font-semibold mono uppercase tracking-widest text-[11px] pt-1">
              So join a group or make your own!
            </p>
          </Section>
        </div>

        <div className="px-5 py-3 border-t border-white/10 bg-white/[0.02] flex justify-end">
          <Button
            data-testid="faq-help-close"
            onClick={() => onOpenChange(false)}
            size="sm"
            className="rounded-none h-9 px-4 mono uppercase text-[11px] tracking-widest bg-red-600 hover:bg-red-500 text-white border border-red-500"
          >
            Got it
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Section({ icon, title, children, testid }) {
  return (
    <section data-testid={testid} className="border border-white/10 bg-white/[0.02] p-4 space-y-2">
      <h3 className="mono uppercase tracking-widest text-[11px] text-zinc-300 flex items-center gap-2">
        {icon}
        {title}
      </h3>
      <div className="space-y-2">{children}</div>
    </section>
  );
}
